import React, { useState, useMemo } from 'react'
import Portal from 'components/Portal/Portal'
import { Modal } from 'components/Modal/Modal'
import { Spinner } from 'components/Loaders/Loaders'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { UPDATE_USER_PASSWORD } from '../../../requests'
import { useDispatch } from 'react-redux'
import { setErrorToast, setSuccessToast } from '../../../actions'

const ChangePassword = ({ editItem, showChangeStaffPassword, setShowChangeStaffPassword }) => {
  const dispatch = useDispatch()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [validError, setValidError] = useState(undefined)
  const [isNewPasswordFocused, setIsNewPasswordFocused] = useState(true)
  const [isConfirmPasswordFocused, setIsConfirmPasswordFocused] = useState(true)
  const [showSpinner, setShowSpinner] = useState(false) // MUST BE FALSE

  const closePopup = () => {
    setNewPassword('')
    setConfirmPassword('')
    setShowChangeStaffPassword(false)
    setIsNewPasswordFocused(true)
    setIsConfirmPasswordFocused(true)
  }

  const isPasswordValid = useMemo(() => {
    const trimmedPW = newPassword.trim().replace(' ', '')
    let errorMessage = ''
    let lowerCaseLetters = /[a-z]/g
    let upperCaseLetters = /[A-Z]/g
    let numbers = /[0-9]/g
    let special = /[!@#$%^&*]/g
    if (trimmedPW.length < 12) errorMessage += 'Must be at least 12 characters'
    if (!lowerCaseLetters.test(trimmedPW))
      errorMessage +=
        errorMessage.length > 0 ? ', include a lowercase letter' : 'Must include a lowercase letter'
    if (!upperCaseLetters.test(trimmedPW))
      errorMessage +=
        errorMessage.length > 0
          ? ', include an uppercase letter'
          : 'Must include an uppercase letter'
    if (!numbers.test(trimmedPW))
      errorMessage +=
        errorMessage.length > 0 ? ', include a number 0-9' : 'Must include a number 0-9'
    if (!special.test(trimmedPW))
      errorMessage +=
        errorMessage.length > 0
          ? ', include a special character'
          : 'Must include a special character'
    if (errorMessage.length > 0) {
      errorMessage += '.'
      setValidError(errorMessage)
      return false
    }

    setValidError('')
    return true
  }, [newPassword])

  const isPasswordConfirmed = useMemo(() => {
    return newPassword === confirmPassword
  }, [newPassword, confirmPassword])

  const saveNewPassword = () => {
    if (!isPasswordValid || !isPasswordConfirmed) return

    setShowSpinner(true)
    UPDATE_USER_PASSWORD(
      { UserId: editItem.UserId, Password: newPassword },
      (updatedUser) => {
        dispatch(setSuccessToast('Successfully Updated Password'))
        setShowSpinner(false)
        closePopup()
      },
      (err) => {
        console.error(err)
        setShowSpinner(false)
        dispatch(setErrorToast(err.message ? err.message : 'Failed to Update Password.'))
      }
    )
  }

  return (
    <Portal isOpened={showChangeStaffPassword}>
      <Modal
        title={`Change Password: ${editItem.Username}`}
        cancelButton
        autoHeight
        modalSize={'medium'}
        closeModal={closePopup}
      >
        {showSpinner ? (
          <div
            style={{
              marginTop: '40px',
              marginBottom: '40px',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Spinner size="large" themeColor="dark-neutral" />
          </div>
        ) : (
          <div className="change-password-modal">
            <TextInput
              label={
                <span>
                  <span style={{ color: `hsl(345,100%,42%)` }}>*</span>New Password
                </span>
              }
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value)
              }}
              onFocus={() => {
                setIsConfirmPasswordFocused(true)
              }}
              onBlur={() => {
                setIsNewPasswordFocused(false)
              }}
              valid={!isNewPasswordFocused ? isPasswordValid : undefined}
              errorMessage={!isNewPasswordFocused && validError ? validError : undefined}
              type="password"
            />
            <TextInput
              customCss={{ marginTop: '5px' }}
              label={
                <span>
                  <span style={{ color: `hsl(345,100%,42%)` }}>*</span>Confirm Password
                </span>
              }
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value)
              }}
              onFocus={() => {
                setIsConfirmPasswordFocused(false)
              }}
              valid={!isConfirmPasswordFocused ? isPasswordConfirmed : undefined}
              description={
                !isConfirmPasswordFocused && isPasswordConfirmed ? 'Password Confirmed' : undefined
              }
              type="password"
            />
            <div className="context-buttons">
              <ContextButton variant="secondary" onClick={closePopup}>
                Cancel
              </ContextButton>
              <ContextButton onClick={saveNewPassword} variant="primary">
                Save New Password
              </ContextButton>
            </div>
          </div>
        )}
      </Modal>
    </Portal>
  )
}

export default ChangePassword
